import { useState,useEffect } from "react";
import { ChevronRightIcon } from "@heroicons/react/solid";
import ProjectDrawer from "./projectDrawer";
import { updateProjectPaymentTerms } from "../api/project-api";
import { useSnackbar } from "react-simple-snackbar";

function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(" ");
}

export default function ProjectTable(props:{ projectList: any[] }) {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [projects, setProjects] = useState(props.projectList);
    const [editingPaymentTerms, setEditingPaymentTerms] = useState<{[key: string]: boolean}>({});
    const [tempPaymentTerms, setTempPaymentTerms] = useState<{[key: string]: string}>({});
    const [openSnackbar] = useSnackbar();
    
    const onOpen = () => {
        setSidebarOpen(true);
    };

    const onClose = () => {
        setSidebarOpen(false);
    };
    
    useEffect(() => {
        setProjects(props.projectList)
    }, [props.projectList])

    const handleEditPaymentTerms = (projectId: string, currentValue: string) => {
        setEditingPaymentTerms(prev => ({ ...prev, [projectId]: true }));
        setTempPaymentTerms(prev => ({ ...prev, [projectId]: currentValue || '' }));
    };

    const handleSavePaymentTerms = async (project: any) => {
        try {
            await updateProjectPaymentTerms(project.pid, tempPaymentTerms[project.id] || '');
            
            // Update local state
            setProjects(prev => prev.map(p => 
                p.id === project.id 
                    ? { ...p, payment_terms: tempPaymentTerms[project.id] }
                    : p
            ));
            
            setEditingPaymentTerms(prev => ({ ...prev, [project.id]: false }));
            openSnackbar('Payment terms updated successfully');
        } catch (error) {
            console.error('Error updating payment terms:', error);
            openSnackbar('Error updating payment terms');
        }
    };

    const handleCancelEdit = (projectId: string) => {
        setEditingPaymentTerms(prev => ({ ...prev, [projectId]: false }));
        setTempPaymentTerms(prev => ({ ...prev, [projectId]: '' }));
    };
    

    return (
        <>
            {/* <ProjectDrawer open={sidebarOpen} onClose={onClose} /> */}

            <div className="min-h-full">
                {/* Static sidebar for desktop */}
                <main className="flex-1">
                    {/* Page title & actions */}

                    {/* Projects list (only on smallest breakpoint) */}
                    <div className="mt-0 sm:hidden">
                        <div className="px-4 sm:px-6">
                            <h2 className="text-xs font-medium tracking-wide text-gray-500 uppercase">Projects</h2>
                        </div>
                        <ul role="list" className="mt-3 border-t border-gray-200 divide-y divide-gray-100">
                            {projects.map((project) => (
                                <li key={project.id}>
                                    <div className="flex items-center justify-between px-4 py-4 group hover:bg-gray-50 sm:px-6">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 truncate">
                                                <span className={"w-2.5 h-2.5 flex-shrink-0 rounded-full"} aria-hidden="true" />
                                                <span className="text-sm font-medium leading-6 truncate">
                                                    {project.name} 
                                                </span>
                                            </div>
                                            <div className="mt-2">
                                                <span className="text-xs text-gray-500">Payment Terms: </span>
                                                {editingPaymentTerms[project.id] ? (
                                                    <div className="flex items-center space-x-2 mt-1">
                                                        <input
                                                            type="text"
                                                            value={tempPaymentTerms[project.id] || ''}
                                                            onChange={(e) => setTempPaymentTerms(prev => ({ ...prev, [project.id]: e.target.value }))}
                                                            className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 flex-1"
                                                            placeholder="Enter payment terms"
                                                            autoFocus
                                                        />
                                                        <button
                                                            onClick={() => handleSavePaymentTerms(project)}
                                                            className="px-2 py-1 text-xs text-white bg-green-600 rounded hover:bg-green-700"
                                                        >
                                                            Save
                                                        </button>
                                                        <button
                                                            onClick={() => handleCancelEdit(project.id)}
                                                            className="px-2 py-1 text-xs text-white bg-gray-600 rounded hover:bg-gray-700"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center space-x-2">
                                                        <span className="text-sm">{project.payment_terms || 'Not set'}</span>
                                                        <button
                                                            onClick={() => handleEditPaymentTerms(project.id, project.payment_terms)}
                                                            className="text-xs text-blue-600 hover:text-blue-800"
                                                        >
                                                            Edit
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <ChevronRightIcon className="w-5 h-5 ml-4 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Projects table (small breakpoint and up) */}
                    <div className="hidden mt-8 sm:block">
                        <div className="inline-block min-w-full align-middle border-b border-gray-200">
                            <table className="min-w-full">
                                <thead>
                                    <tr className="border-t border-gray-200">
                                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                                            <span className="lg:pl-2">Project</span>
                                        </th>
                                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">Payment Terms</th>
                                        <th className="py-3 pr-6 text-xs font-medium tracking-wider text-right text-gray-500 uppercase border-b border-gray-200 bg-gray-50" />
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-100">
                                    {projects.map((project) => (
                                        <tr key={project.id}>
                                            <td className="w-full px-6 py-3 text-sm font-medium text-gray-900 max-w-0 whitespace-nowrap">
                                                <div className="flex items-center space-x-3 lg:pl-2">
                                                    <div className={classNames(project.bgColorClass, "flex-shrink-0 w-2.5 h-2.5 rounded-full")} aria-hidden="true" />
                                                    <a href="#" className="truncate hover:text-gray-600">
                                                        <span>
                                                            {project.name} 
                                                        </span>
                                                    </a>
                                                </div>
                                            </td>
                                            <td className="px-6 py-3 text-sm font-medium text-gray-500">
                                                <div className="flex items-center space-x-2">
                                                    {editingPaymentTerms[project.id] ? (
                                                        <div className="flex items-center space-x-2">
                                                            <input
                                                                type="text"
                                                                value={tempPaymentTerms[project.id] || ''}
                                                                onChange={(e) => setTempPaymentTerms(prev => ({ ...prev, [project.id]: e.target.value }))}
                                                                className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                                                placeholder="Enter payment terms"
                                                                autoFocus
                                                            />
                                                            <button
                                                                onClick={() => handleSavePaymentTerms(project)}
                                                                className="px-2 py-1 text-xs text-white bg-green-600 rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                                                            >
                                                                Save
                                                            </button>
                                                            <button
                                                                onClick={() => handleCancelEdit(project.id)}
                                                                className="px-2 py-1 text-xs text-white bg-gray-600 rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center space-x-2">
                                                            <span>{project.payment_terms || 'Not set'}</span>
                                                            <button
                                                                onClick={() => handleEditPaymentTerms(project.id, project.payment_terms)}
                                                                className="px-2 py-1 text-xs text-blue-600 hover:text-blue-800 focus:outline-none"
                                                            >
                                                                Edit
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="hidden px-6 py-3 text-sm text-right text-gray-500 md:table-cell whitespace-nowrap">{project.lastUpdated}</td>
                                            {/* <td className="px-6 py-3 text-sm font-medium text-right whitespace-nowrap">
                                                <a href="#" className="text-indigo-600 hover:text-indigo-900" onClick={onOpen}>
                                                    Details
                                                </a>
                                            </td> */}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
}
